import {gql} from '@apollo/client'
import React, {useState} from 'react'
import {Grants} from '.'
import {Button} from '../../../components/library/buttons'
import {Checkbox} from '../../../components/library/form/checkbox'
import {
  List,
  ListColumns,
  NoResultsPermissions
} from '../../../components/library/list'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {OrganisationNode} from '../../../organisation'
import {EnvironmentAccess} from './environment-access'
import {ListRolesOrganisationFragment} from './generated/list-roles'

export const ListRoles = ({
  grants,
  setGrants,
  environmentNodes,
  roles,
  loading,
  organisation
}: {
  grants?: Grants
  setGrants: (update: Grants) => void
  environmentNodes: ListRolesOrganisationFragment['environments']['nodes']
  roles: ListRolesOrganisationFragment['roles']['nodes']
  loading?: boolean
  organisation: OrganisationNode
}) => {
  const canViewRoles = organisation.hasPermission(
    PermissionResource.CustomRole,
    PermissionAction.View
  )
  const canEdit = organisation.hasPermission(
    PermissionResource.Group,
    PermissionAction.Edit
  )
  const [environmentModal, setEnvironmentModal] = useState<string>()
  const columns: ListColumns<
    ListRolesOrganisationFragment['roles']['nodes'][0]
  > = [
    {
      id: 'role',
      header: 'Role',
      accessor: r => {
        const roleId = r.id
        const checked =
          (grants && roleId in grants && grants[roleId].active) || false
        return (
          <Checkbox
            checked={checked}
            disabled={!canEdit}
            onChange={() => {
              const active = !checked
              setGrants(
                grants
                  ? {
                      ...grants,
                      [roleId]: {
                        ...(roleId in grants
                          ? grants[roleId] // set active, spread existing state
                          : {environments: null}), // create new record, default environments to null (all)
                        active
                      }
                    }
                  : {[roleId]: {active, environments: null}} // create new record, default environments to null (all)
              )
            }}
            label={r.name}
          />
        )
      }
    },
    {
      id: 'description',
      header: 'Description',
      accessor: r => r.description
    },
    {
      id: 'environment',
      header: 'Environments',
      accessor: r => {
        const minWidth = '150px' // stops column headers jumping around
        const roleId = r.id
        const checked = grants && roleId in grants && grants[roleId].active
        if (!checked) return <div style={{minWidth}} />
        const environmentIds =
          (grants && roleId in grants && grants[roleId].environments) || []
        const names = environmentIds
          .map(id => environmentNodes.find(e => e.id === id)?.name)
          .filter((name): name is string => !!name)
        const display = names.length
          ? names.join(', ')
          : checked
          ? 'All environments'
          : ''

        return (
          <div style={{minWidth}}>
            {display ? (
              <Button
                appearance="transparent"
                disabled={!canEdit}
                onClick={() => setEnvironmentModal(roleId)}
              >
                {display}
              </Button>
            ) : null}
          </div>
        )
      }
    }
  ]

  return (
    <>
      <List
        loading={loading && !roles}
        bleed={'#f2f2f2'}
        columns={columns}
        rows={roles}
        renderEmpty={canViewRoles ? undefined : () => <NoResultsPermissions />}
      />
      {environmentModal && grants && environmentModal in grants && (
        <EnvironmentAccess
          environments={environmentNodes}
          selectedEnvironments={grants[environmentModal].environments}
          handleClose={() => setEnvironmentModal(undefined)}
          onUpdate={environments => {
            const roleId = environmentModal
            setGrants(
              grants
                ? {
                    ...grants,
                    [roleId]: {
                      ...(roleId in grants ? grants[roleId] : {active: true}),
                      environments
                    }
                  }
                : {[roleId]: {active: true, environments}}
            )
            setEnvironmentModal(undefined)
          }}
        />
      )}
    </>
  )
}

ListRoles.fragments = {
  organisation: gql`
    fragment ListRolesOrganisation on Organisation {
      roles: customRoles {
        nodes {
          id
          name
          description
        }
      }
      environments {
        nodes {
          id
          name
          active
        }
      }
    }
  `
}
